import { useEffect, useState } from 'react';
import { Body1, Body1Strong, Button, Caption1, Tag, makeStyles, tokens } from '@fluentui/react-components';
import { ChevronDownRegular, ChevronUpRegular, ClipboardTaskListLtrRegular } from '@fluentui/react-icons';

import { TChangeTemplate, TTemplateInputValue } from '../../models';
import { resolveTemplateString } from '../../utils';
import { TaskDocumentLinks } from '../shared/TaskDocumentLinks';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  summary: { display: 'flex', flexDirection: 'column', gap: '2px' },
  summaryLine: { color: tokens.colorNeutralForeground2 },
  detailsButton: { alignSelf: 'flex-start', paddingLeft: 0 },
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    marginTop: tokens.spacingVerticalXS,
  },
  group: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  tags: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalXS },
  tag: { fontWeight: tokens.fontWeightRegular },
  activityList: { listStyleType: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column' },
  activityItem: {
    display: 'grid',
    gridTemplateColumns: '20px minmax(0, 1fr)',
    gap: tokens.spacingHorizontalS,
    alignItems: 'start',
    padding: `${tokens.spacingVerticalXS} 0`,
  },
  activityIcon: { color: tokens.colorNeutralForeground3, marginTop: '2px' },
  activityText: { display: 'flex', flexDirection: 'column', gap: '2px' },
  muted: { color: tokens.colorNeutralForeground3 },
});

const SummaryLine = ({ count, singular, plural }: { count: number; singular: string; plural: string }) => {
  const classes = useStyles();
  if (count === 0) return null;
  return (
    <Caption1 className={classes.summaryLine}>
      {count} {count === 1 ? singular : plural}
    </Caption1>
  );
};

const MAX_VISIBLE_ACTIVITIES = 3;
const MAX_VISIBLE_CATEGORIES = 5;

type TProps = {
  template: TChangeTemplate;
  inputValues: Record<string, TTemplateInputValue>;
};

// A compact summary of what the template prepares beyond the request text. Details
// stay behind progressive disclosure so the setup stays a quick start. Tokenized
// activity titles resolve against the entered inputs.
export const PreparedByTemplate = ({ template, inputValues }: TProps) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);

  useEffect(() => {
    setExpanded(false);
    setShowAllActivities(false);
    setShowAllCategories(false);
  }, [template.id]);

  const inputs = template.creationInputs ?? [];
  const activities = template.suggestedTasks.map((task) => ({
    title: resolveTemplateString(task.title, inputValues, inputs, 'preview'),
    assignedFunction: task.assignedFunction,
    documents: task.documents?.map((document) => ({
      ...document,
      name: resolveTemplateString(document.name, inputValues, inputs, 'preview'),
    })),
  }));
  const visibleActivities = showAllActivities ? activities : activities.slice(0, MAX_VISIBLE_ACTIVITIES);
  const hiddenActivityCount = activities.length - visibleActivities.length;

  const categories = template.suggestedAffectedObjectCategories;
  const visibleCategories = showAllCategories ? categories : categories.slice(0, MAX_VISIBLE_CATEGORIES);
  const hiddenCategoryCount = categories.length - visibleCategories.length;

  return (
    <div className={classes.root}>
      <Body1Strong>Prepared by this template</Body1Strong>
      <div className={classes.summary}>
        <SummaryLine
          count={template.assessmentSections.length}
          singular="assessment section"
          plural="assessment sections"
        />
        <SummaryLine
          count={template.suggestedRoles.length}
          singular="suggested function"
          plural="suggested functions"
        />
        <SummaryLine count={activities.length} singular="preliminary activity" plural="preliminary activities" />
        <SummaryLine
          count={categories.length}
          singular="candidate affected-object category"
          plural="candidate affected-object categories"
        />
      </div>

      <Button
        className={classes.detailsButton}
        appearance="transparent"
        size="small"
        icon={expanded ? <ChevronUpRegular /> : <ChevronDownRegular />}
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
      >
        {expanded ? 'Hide details' : 'View details'}
      </Button>

      {expanded ? (
        <div className={classes.details}>
          {template.assessmentSections.length > 0 ? (
            <div className={classes.group}>
              <Caption1 className={classes.muted}>Assessment sections</Caption1>
              <div className={classes.tags}>
                {template.assessmentSections.map((section) => (
                  <Tag key={section} className={classes.tag} appearance="outline" shape="rounded" size="small">
                    {section}
                  </Tag>
                ))}
              </div>
            </div>
          ) : null}

          {template.suggestedRoles.length > 0 ? (
            <div className={classes.group}>
              <Caption1 className={classes.muted}>Suggested functions</Caption1>
              <div className={classes.tags}>
                {template.suggestedRoles.map((role) => (
                  <Tag key={role} className={classes.tag} appearance="outline" shape="rounded" size="small">
                    {role}
                  </Tag>
                ))}
              </div>
            </div>
          ) : null}

          {activities.length > 0 ? (
            <div className={classes.group}>
              <Caption1 className={classes.muted}>Preliminary activities</Caption1>
              <ul className={classes.activityList}>
                {visibleActivities.map((activity) => (
                  <li className={classes.activityItem} key={activity.title}>
                    <ClipboardTaskListLtrRegular className={classes.activityIcon} />
                    <div className={classes.activityText}>
                      <Body1>{activity.title}</Body1>
                      <Caption1 className={classes.muted}>{activity.assignedFunction}</Caption1>
                      <TaskDocumentLinks documents={activity.documents} />
                    </div>
                  </li>
                ))}
              </ul>
              {hiddenActivityCount > 0 ? (
                <Button appearance="transparent" size="small" onClick={() => setShowAllActivities(true)}>
                  +{hiddenActivityCount} more
                </Button>
              ) : showAllActivities && activities.length > MAX_VISIBLE_ACTIVITIES ? (
                <Button appearance="transparent" size="small" onClick={() => setShowAllActivities(false)}>
                  Show fewer
                </Button>
              ) : null}
            </div>
          ) : null}

          {categories.length > 0 ? (
            <div className={classes.group}>
              <Caption1 className={classes.muted}>Candidate affected-object categories</Caption1>
              <div className={classes.tags}>
                {visibleCategories.map((category) => (
                  <Tag key={category} className={classes.tag} appearance="outline" shape="rounded" size="small">
                    {category}
                  </Tag>
                ))}
                {hiddenCategoryCount > 0 ? (
                  <Tag
                    as="button"
                    className={classes.tag}
                    appearance="outline"
                    shape="rounded"
                    size="small"
                    onClick={() => setShowAllCategories(true)}
                  >
                    +{hiddenCategoryCount} more
                  </Tag>
                ) : showAllCategories && categories.length > MAX_VISIBLE_CATEGORIES ? (
                  <Tag
                    as="button"
                    className={classes.tag}
                    appearance="outline"
                    shape="rounded"
                    size="small"
                    onClick={() => setShowAllCategories(false)}
                  >
                    Show fewer
                  </Tag>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
